import {
  Box,
  createStyles,
  Divider,
  Grid,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import classNames from 'classnames'
import React, { ReactElement } from 'react'
import {
  DateField,
  EditButton,
  FunctionField,
  ReduxState,
  ReferenceField,
  ResourceContextProvider,
  Show,
  ShowProps,
  Tab,
  TabbedShowLayout,
  TabbedShowLayoutTabs,
  TextField,
  TextInput,
  TitleProps,
  sanitizeFetchType,
  useQuery,
  useTranslate,
} from 'react-admin'
import { useSelector } from 'react-redux'
import { Authority } from '../../../core/auth/Authority'
import { ResourceName } from '../../../core/ResourceName'
import RelationTab from '../../common/RelationTab'
import relationTabFilter from '../../common/RelationTabFilter'
import { useHasAuthority } from '../../hooks/useHasAuthority'
import { DeviceStatusesTab } from '../common/modules/ModuleStatus'
import { DoorDto } from '../../../core/dto/device/doors/door.dto'
import { DoorForceReportStateButton } from './door-show/DoorForceReportStateButton'
import { DoorResetModulesButton } from './door-show/DoorResetModulesButton'
import { DoorStandbyModeButton } from './door-show/DoorStandbyModeButton'
import { DeviceTypes } from '../../../core/enum/DeviceTypes'
import { DoorSetWorkingModeButton } from './door-show/DoorSetWorkingModeButton'
import { DoorTypes } from '../../../core/enum/DoorTypes'
import { DoorReleaseButton } from './door-show/DoorReleaseButton'
import { SystemLogsList } from '../../system-notifications/system-logs/SystemLogsList'
import { SystemLogTypes } from '../../../core/enum/SystemLogTypes'
import { ButtonWithRecurringJob } from '../../common/ButtonWithRecurringJob'
import { BlankActions } from '../../common/BlankActions'

const useStyles = makeStyles((theme: any) =>
  createStyles({
    show: {
      width: `calc(100vw - ${theme?.sidebar?.closedWidth}px - 34px)`,
    },
    showSideOpen: {
      maxWidth: `calc(100vw - ${theme?.sidebar?.width}px - 34px)`,
    },
    showXSmall: {
      width: '100vw',
    },
  }),
)

interface DoorTitleProps extends TitleProps {
  record?: DoorDto
}

const ShowTitle = (data: DoorTitleProps) => {
  const { record: door } = data
  const translate = useTranslate()
  return (
    <span>
      {translate('resources.doors.titles.show')}: {door?.id}. {door?.name}
    </span>
  )
}

const GeneralActions = ({ ...props }) => {
  const { record: door } = props
  const hasAuthority = useHasAuthority()

  return (
    <>
      <Divider />
      <Box p={1}>
        <Grid container direction="row" justify="flex-end" alignItems="center">
          {door?.type !== DoorTypes.NO_READERS && (
            <Grid item style={{ margin: '2px' }}>
              <ButtonWithRecurringJob
                mainAction={<DoorSetWorkingModeButton />}
                recurringAction={<DoorSetWorkingModeButton asRecurringJob />}
              />
            </Grid>
          )}
          {door?.type === DoorTypes.NO_READERS && (
            <Grid item style={{ margin: '2px' }}>
              <DoorReleaseButton />
            </Grid>
          )}
          <Grid item style={{ margin: '2px' }}>
            <ButtonWithRecurringJob
              disabled={!hasAuthority(Authority.DOORS_SET_STANDBY)}
              mainAction={<DoorStandbyModeButton />}
              recurringAction={<DoorStandbyModeButton asRecurringJob />}
            />
          </Grid>
          <Grid item>
            <EditButton
              basePath={`/${ResourceName.DOORS}`}
              record={door}
              variant="contained"
              style={{ margin: '2px' }}
              disabled={!hasAuthority(Authority.EDIT_DOORS)}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

const SetStateActions = () => (
  <>
    <Box p={1} display="block" textAlign="right">
      <Grid container direction="row" justify="flex-end" alignItems="center">
        <Grid item>
          <DoorForceReportStateButton />
        </Grid>
        <Grid item>
          <DoorResetModulesButton />
        </Grid>
      </Grid>
    </Box>
  </>
)

export const DoorTabs = ({ ...props }) => {
  const { simpleView, createdAt, ...rest } = props
  const { basePath, record: door } = rest
  const translate = useTranslate()
  const hasAuthority = useHasAuthority()

  const { data: serverDate } = useQuery(
    {
      type: sanitizeFetchType('getServerDateTime'),
      resource: ResourceName.CURRENT_USER,
      payload: {},
    },
    {
      enabled: !simpleView && !createdAt,
    },
  )

  const eventsFilters = (...filterProps) =>
    relationTabFilter({
      ...filterProps,
      children: [<TextInput source="name" alwaysOn />],
      resource: ResourceName.DOORS,
    })

  const entrancesFilters = (...filterProps) =>
    relationTabFilter({
      ...filterProps,
      children: [<TextInput source="note" alwaysOn />],
      resource: ResourceName.DOORS,
    })

  return (
    <TabbedShowLayout
      {...rest}
      syncWithLocation={!simpleView}
      tabs={
        <TabbedShowLayoutTabs
          variant="scrollable"
          scrollButtons="auto"
          {...rest}
        />
      }
    >
      <Tab label="resources.doors.tabs.general">
        <TextField label="ID" source="id" />
        <TextField source="name" />
        <FunctionField<DoorDto>
          source="isWatched"
          render={(record?: DoorDto) => (
            <div>
              {record?.isWatched
                ? translate('const.yes')
                : translate('const.no')}
            </div>
          )}
        />
        <DateField
          source="lastRefresh"
          showTime
          options={{
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          }}
        />
        <FunctionField<DoorDto>
          source="direction"
          render={(record?: DoorDto) => (
            <div>
              {translate(
                `resources.enums.deviceDirection.${record?.direction}`,
              )}
            </div>
          )}
        />
        <FunctionField<DoorDto>
          source="type"
          render={(record?: DoorDto) => (
            <div>{translate(`resources.enums.DoorTypes.${record?.type}`)}</div>
          )}
        />
        {door?.categoryId && hasAuthority(Authority.VIEW_DEVICE_CATEGORIES) && (
          <ReferenceField
            source="categoryId"
            reference={ResourceName.DEVICE_CATEGORIES}
            link="show"
          >
            <TextField source="hierarchyString" />
          </ReferenceField>
        )}
        {door?.cameraId && hasAuthority(Authority.VIEW_CAMERAS) && (
          <ReferenceField
            source="cameraId"
            reference={ResourceName.CAMERAS}
            link="show"
          >
            <TextField source="description" />
          </ReferenceField>
        )}
        <FunctionField<DoorDto>
          source="stanbyMode"
          render={(record?: DoorDto) => (
            <div>
              {translate(`resources.enums.standbyMode.${record?.stanbyMode}`)}
            </div>
          )}
        />
        <FunctionField<DoorDto>
          source="onlineInfixCeck"
          render={(record?: DoorDto) => (
            <div>
              {record?.onlineInfixCeck
                ? translate('const.yes')
                : translate('const.no')}
            </div>
          )}
        />
        <FunctionField<DoorDto>
          source="multiplicity"
          render={(record?: DoorDto) => (
            <div>
              {translate(
                `resources.enums.multiplicities.${record?.multiplicity}`,
              )}
            </div>
          )}
        />
        {door?.tsCanId && hasAuthority(Authority.VIEW_TS_CANS) && (
          <ReferenceField
            source="tsCanId"
            reference={ResourceName.TS_CANS}
            link="show"
          >
            <TextField source="name" />
          </ReferenceField>
        )}
        {door?.supervisorId && hasAuthority(Authority.VIEW_PALMS) && (
          <ReferenceField
            source="supervisorId"
            reference={ResourceName.PALMS}
            link="show"
          >
            <FunctionField
              source="name"
              render={(palm) => `${palm?.id}. ${palm?.name}`}
            />
          </ReferenceField>
        )}
        {(!simpleView && <GeneralActions record={door} />) || <BlankActions />}
      </Tab>
      {hasAuthority(Authority.VIEW_SYSTEM_LOGS) && !simpleView && (
        <Tab path="logs" label={translate('resources.doors.tabs.logs')}>
          <div>
            <ResourceContextProvider value={ResourceName.SYSTEM_LOGS}>
              <SystemLogsList
                basePath={basePath}
                filter={{
                  type: SystemLogTypes.DOOR_LOG,
                  doorId: door?.id,
                }}
                filters={undefined}
                actions={false}
                title=" "
              />
            </ResourceContextProvider>
          </div>
        </Tab>
      )}
      {hasAuthority(Authority.VIEW_EVENTS) && !simpleView && (
        <Tab path="events" label={translate('resources.doors.tabs.events')}>
          <>
            <RelationTab<DoorDto>
              resource={ResourceName.EVENTS}
              source="eventsIds"
              mode={hasAuthority(Authority.EDIT_DOORS) ? 'edit' : 'show'}
              attachMethod="attachEvents"
              detachMethod="detachEvents"
              attachRequestPayload={(r, ids) => ({
                doorId: r.id,
                eventsIds: ids,
              })}
              detachRequestPayload={(r, ids) => ({
                doorId: r.id,
                eventsIds: ids,
              })}
              refetchListAfterChange={(filters) =>
                filters?.filterVerifierDeviceId === true
              }
              filters={eventsFilters() as ReactElement[]}
              filterDefaultValues={{
                verifierDeviceId: door?.id,
                filterVerifierDeviceId: false,
              }}
              sort={{
                field: 'id',
                order: 'DESC',
              }}
            >
              <TextField label="ID" source="id" />
              <TextField source="name" />
            </RelationTab>
          </>
        </Tab>
      )}
      {hasAuthority(Authority.VIEW_ENTRANCES) && !simpleView && (
        <Tab
          path="entrances"
          label={translate('resources.doors.tabs.entrances')}
        >
          <>
            <RelationTab<DoorDto>
              resource={ResourceName.ENTRANCES}
              source="entrancesIds"
              mode={hasAuthority(Authority.EDIT_DOORS) ? 'edit' : 'show'}
              attachMethod="attachEntrances"
              detachMethod="detachEntrances"
              attachRequestPayload={(r, ids) => ({
                doorId: r.id,
                entrancesIds: ids,
              })}
              detachRequestPayload={(r, ids) => ({
                doorId: r.id,
                entrancesIds: ids,
              })}
              refetchListAfterChange={(filters) =>
                filters?.filterVerifierDeviceId === true
              }
              filters={entrancesFilters() as ReactElement[]}
              filterDefaultValues={{
                verifierDeviceId: door?.id,
                filterVerifierDeviceId: false,
              }}
              sort={{
                field: 'id',
                order: 'DESC',
              }}
            >
              <TextField label="ID" source="id" />
              <TextField source="note" />
            </RelationTab>
          </>
        </Tab>
      )}
      {door?.jsonStatus && (
        <Tab label="resources.doors.tabs.statuses" path="statuses">
          <DeviceStatusesTab
            jsonStatus={door.jsonStatus}
            deviceType={DeviceTypes.DOORS}
            referenceDateTime={createdAt || serverDate?.serverDateTime}
          />
          {!simpleView && <SetStateActions />}
        </Tab>
      )}
    </TabbedShowLayout>
  )
}

export const DoorShow = (props: ShowProps) => {
  const classes = useStyles()

  const sidebarOpen = useSelector(
    (state: ReduxState) => state.admin.ui.sidebarOpen,
  )

  const theme = useTheme()
  const xSmallScreen = useMediaQuery(theme.breakpoints.down('xs'))

  return (
    <Show
      {...props}
      actions={false}
      title={<ShowTitle />}
      className={
        !xSmallScreen
          ? classNames(classes.show, sidebarOpen && classes.showSideOpen)
          : classes.showXSmall
      }
    >
      <DoorTabs />
    </Show>
  )
}
